import RAW from '../data/christmasGame.json';

const DATE_KEYS = ['start', 'end', 'draw'];

const dateText = (iso, withYear) =>
  new Date(`${iso}T12:00:00+02:00`).toLocaleDateString('bg-BG', {
    timeZone: 'Europe/Sofia', day: 'numeric', month: 'long', ...(withYear && { year: 'numeric' }),
  });

/**
 * Текстовете в christmasGame.json пишат датите и числата като {start},
 * {endYear}, {ticketsPerOrder}…, за да се сменят на едно място. Същата
 * функция има в scripts/site-data.mjs за статичните страници.
 */
export function fillText(value, game = RAW) {
  if (typeof value === 'string') {
    return value.replace(/\{(\w+?)(Year)?\}/g, (whole, key, year) => {
      if (DATE_KEYS.includes(key)) return dateText(game[key], !!year);
      return typeof game[key] === 'number' ? String(game[key]) : whole;
    });
  }
  if (Array.isArray(value)) return value.map(v => fillText(v, game));
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, fillText(v, game)]));
  }
  return value;
}

export const GAME = fillText(RAW);

const DAY_MS = 86400000;

// Ноември и декември са изцяло в зимно часово време (UTC+2), затова денят на
// играта е „полунощ по българско време“ без да мислим за лятното. Правилата в
// firestore.rules смятат деня по същата формула – сменяш ли едното, смени и
// другото, иначе сървърът ще отказва всяко завъртане.
const BG_OFFSET_MS = 2 * 3600000;

export const ADMIN_EMAIL = 'stamsy.art@abv.bg';
export const SPINS = 'ChristmasSpins';
export const BONUS = 'ChristmasBonus';
export const DRAWS = 'ChristmasDraw';
export const INVITES = 'ChristmasInvites';

/** Параметърът в линка за покана: /koledna-igra?pokana=<uid на поканилия>. */
export const INVITE_PARAM = 'pokana';

/** Firebase uid – същата проверка има в firestore.rules. */
export const isInviteCode = code => typeof code === 'string' && /^[A-Za-z0-9]{10,128}$/.test(code);

/** Билетите от покани – по {ticketsPerInvite} за до {maxInvites} поканени. */
export const inviteTickets = count => Math.min(count, GAME.maxInvites) * GAME.ticketsPerInvite;

/** Пореден номер на българския календарен ден, в който попада `ms`. */
export const bgDay = ms => Math.floor((ms + BG_OFFSET_MS) / DAY_MS);

/** `YYYY-MM-DD` → пореден номер на деня. */
export const dayOf = isoDate => bgDay(Date.parse(`${isoDate}T00:00:00+02:00`));

/** Кога започва следващият български ден, в ms. */
export const nextDayStart = ms => (bgDay(ms) + 1) * DAY_MS - BG_OFFSET_MS;

export const dayStart = day => day * DAY_MS - BG_OFFSET_MS;

/** „1 ноември“ – датата на деня по български. */
export const dateLabel = (day, opts = { day: 'numeric', month: 'long' }) =>
  new Date(dayStart(day) + 12 * 3600000)
    .toLocaleDateString('bg-BG', { timeZone: 'Europe/Sofia', ...opts });

export const START_DAY = dayOf(GAME.start);
export const END_DAY = dayOf(GAME.end);
export const DRAW_DAY = dayOf(GAME.draw);
export const ANNOUNCE_DAY = dayOf(GAME.announceFrom);

/**
 * 'hidden' – още не рекламираме играта
 * 'soon'   – обявена, но колелото още не се върти
 * 'live'   – колелото се върти
 * 'ended'  – завъртанията приключиха, чакаме или сме направили тегленето
 */
export function phaseAt(ms) {
  const day = bgDay(ms);
  if (day < ANNOUNCE_DAY) return 'hidden';
  if (day < START_DAY) return 'soon';
  if (day <= END_DAY) return 'live';
  return 'ended';
}

/**
 * Банерът и линкът в менюто стоят до края на деня на тегленето – и само ако
 * играта е публикувана (`published` в christmasGame.json).
 */
export const isPromoted = (ms, published = GAME.published) => {
  if (!published) return false;
  const phase = phaseAt(ms);
  return phase === 'soon' || phase === 'live' || (phase === 'ended' && bgDay(ms) <= DRAW_DAY);
};

export const spinId = (uid, day) => `${uid}_${day}`;

// 32-битово разбъркване (murmur3 finaliser). Микросекундите на сървърния
// timestamp са на практика случайни, но емулаторът пази само милисекунди –
// без разбъркване всяко завъртане там би паднало на едно и също поле.
function mix(a, b) {
  let h = (a ^ Math.imul(b >>> 0, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

const TOTAL_WEIGHT = GAME.segments.reduce((sum, s) => sum + s.weight, 0);

/**
 * Кое поле се е паднало. Изчислява се само от сървърния час на записа, който
 * браузърът не може да избере, и от него не може да се върне назад – записът
 * е единствен за деня и правилата не позволяват да се промени или изтрие.
 */
export function segmentIndexFor(at) {
  let roll = mix(at.seconds >>> 0, at.nanoseconds >>> 0) % TOTAL_WEIGHT;
  for (let i = 0; i < GAME.segments.length; i++) {
    roll -= GAME.segments[i].weight;
    if (roll < 0) return i;
  }
  return GAME.segments.length - 1;
}

const GIFTS = Object.entries(GAME.gifts);
const GIFT_TOTAL = GIFTS.reduce((sum, [, w]) => sum + w, 0);

/**
 * Кой подарък се пада, когато стрелката спре на „Подарък“. Второ, независимо
 * разбъркване на същия сървърен час – пак не може да се избере от браузъра.
 */
export function giftFor(at) {
  let roll = mix((at.seconds ^ 0x27d4eb2d) >>> 0, (at.nanoseconds ^ 0x165667b1) >>> 0) % GIFT_TOTAL;
  for (const [key, weight] of GIFTS) {
    roll -= weight;
    if (roll < 0) return key;
  }
  return GIFTS[GIFTS.length - 1][0];
}

/** Отместване в градуси вътре в полето, за да не спира стрелката винаги в средата. */
export function jitterFor(at, sliceDeg) {
  const unit = mix(at.nanoseconds >>> 0, at.seconds >>> 0) / 0xffffffff;
  return (unit - 0.5) * sliceDeg * 0.6;
}

/**
 * Следващото завъртане на колелото в градуси. Винаги напред и поне `turns`
 * пълни обиколки, така че полето `index` да застане под стрелката горе.
 */
export function wheelTarget(currentDeg, index, count, jitterDeg = 0, turns = 5) {
  const slice = 360 / count;
  const wanted = (360 - (index * slice + slice / 2 + jitterDeg)) % 360;
  const now = ((currentDeg % 360) + 360) % 360;
  const delta = ((wanted - now) + 360) % 360;
  return currentDeg + turns * 360 + delta;
}

// FNV-1a – кратък код, който администраторът вижда същия в своята страница.
function fnv1a(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export const rewardCode = id =>
  'SA-' + fnv1a(id).toString(36).toUpperCase().padStart(7, '0').slice(-6);

/** „10% отстъпка – за всяка поръчка“ – един ред за правилата. */
export const rewardLine = reward => `${reward.title} – ${reward.condition}`;

/**
 * Какво е донесло едно завъртане. `spin` е документ от ChristmasSpins.
 * `reward` е кодът, ако има такъв; `miss` – „Опитай пак“ или „Усмивка“.
 */
export function outcomeOf(spin) {
  const index = segmentIndexFor(spin.at);
  const segment = GAME.segments[index];
  const key = segment.reward === 'gift' ? giftFor(spin.at) : segment.reward;
  const def = { key, ...GAME.rewards[key] };
  return {
    index,
    segment,
    tickets: GAME.ticketsPerSpin,
    reward: def.noCode ? null : {
      ...def,
      code: rewardCode(spin.id),
      // Денят на завъртането се брои за първи.
      validUntil: spin.day + GAME.codeValidDays - 1,
    },
    miss: def.noCode ? def : null,
  };
}

/**
 * Участниците с билетите и кодовете им, подредени по билети. Завъртанията и
 * билетите от поръчки се събират по имейл, поканите – по uid – поръчките идват от чата, където
 * клиентът казва имейла си, а не потребителското си id.
 */
export function summarize(spins, bonuses = [], invites = []) {
  const rows = new Map();
  const rowFor = (email, name, uid) => {
    const key = email.toLowerCase();
    if (!rows.has(key)) {
      rows.set(key, { email: key, name: key, uid: null, spins: 0, orders: 0, invites: 0, tickets: 0, rewards: [], bonuses: [] });
    }
    const row = rows.get(key);
    if (uid && !row.uid) {
      row.uid = uid;
      row.name = name || key;
    }
    return row;
  };

  for (const spin of spins) {
    const row = rowFor(spin.email, spin.name, spin.uid);
    const outcome = outcomeOf(spin);
    row.spins += 1;
    row.tickets += outcome.tickets;
    if (outcome.reward) {
      row.rewards.push({ ...outcome.reward, spinId: spin.id, day: spin.day, redeemed: !!spin.redeemed });
    }
  }
  for (const bonus of bonuses) {
    const row = rowFor(bonus.email);
    row.orders += 1;
    row.tickets += bonus.tickets;
    row.bonuses.push(bonus);
  }

  // Покана се брои, ако поканеният е завъртял за първи път в деня на поканата
  // (не е играл преди) и поканилият също е участник. Документът за покана има
  // id = uid на поканения, затова всеки може да бъде поканен само веднъж.
  const firstDay = new Map();
  const rowByUid = new Map();
  for (const row of rows.values()) if (row.uid) rowByUid.set(row.uid, row);
  for (const spin of spins) {
    if (!firstDay.has(spin.uid) || spin.day < firstDay.get(spin.uid)) firstDay.set(spin.uid, spin.day);
  }
  for (const invite of invites) {
    const inviter = rowByUid.get(invite.inviter);
    if (!inviter || invite.inviter === invite.id || firstDay.get(invite.id) !== invite.day) continue;
    inviter.invites += 1;
  }
  for (const row of rows.values()) row.tickets += inviteTickets(row.invites);

  return [...rows.values()].sort((a, b) => b.tickets - a.tickets);
}

/**
 * Тегли победител с шанс, пропорционален на билетите. `randomBelow(n)` връща
 * цяло число в [0, n) – в браузъра идва от crypto.getRandomValues.
 */
export function pickWinner(rows, randomBelow) {
  const total = rows.reduce((sum, r) => sum + r.tickets, 0);
  if (total === 0) return null;
  let ticket = randomBelow(total);
  for (const row of rows) {
    ticket -= row.tickets;
    if (ticket < 0) return { row, ticket: total + ticket, total };
  }
  return null;
}

/** Безпристрастно случайно число в [0, n) без изместване от модула. */
export function cryptoRandomBelow(n) {
  const limit = Math.floor(0x100000000 / n) * n;
  const buf = new Uint32Array(1);
  do {
    window.crypto.getRandomValues(buf);
  } while (buf[0] >= limit);
  return buf[0] % n;
}

/**
 * Какво има на колелото: печалба, условие и шанс, по веднъж на печалба.
 * Полетата „Подарък“ се разпределят между подаръците по техните тегла.
 * Печалбите са първи, по шанс; полетата без награда – накрая.
 */
export function rewardTable() {
  const weights = new Map();
  const add = (key, w) => weights.set(key, (weights.get(key) || 0) + w);
  for (const s of GAME.segments) {
    if (s.reward === 'gift') {
      for (const [key, w] of GIFTS) add(key, (s.weight * w) / GIFT_TOTAL);
    } else {
      add(s.reward, s.weight);
    }
  }
  return [...weights.entries()]
    .map(([key, weight]) => ({
      key,
      ...GAME.rewards[key],
      percent: Math.round((weight / TOTAL_WEIGHT) * 1000) / 10,
    }))
    .sort((a, b) => (!!a.noCode - !!b.noCode) || (b.percent - a.percent));
}
